import { useTranslation } from 'react-i18next';
import { uniqBy } from 'es-toolkit';

import getFieldErrors from '/shared/functions/getFieldErrors';
import type { FieldError, FieldErrorDetail } from '/shared/functions/getFieldErrors';

import ErrorComponent from './Error';

import StyledErrorsList from '/shared/styled/StyledErrorsList';

interface DisplayFieldError extends FieldError, FieldErrorDetail {
  code: string;
  message?: string;
  tokens?: Record<string, string> | null;
  isTranslated?: boolean;
  description?: string | null;
}

interface Props {
  errors?: readonly FieldError[] | null;
  for?: string | null;
  context?: string | null;
  showDescription?: boolean;
}

function hasDisplayCode(error: FieldError | FieldErrorDetail): error is DisplayFieldError {
  return 'code' in error && typeof error.code === 'string';
}

function FieldErrors({ errors, for: forElement, context, showDescription = true }: Props) {
  const { t, i18n } = useTranslation();
  const fieldErrors = getFieldErrors({ errors, forElement, context }) || [];
  const displayErrors = fieldErrors.filter(hasDisplayCode);
  if (displayErrors.length <= 0) {
    return null;
  }
  return (
    <StyledErrorsList isFieldError>
      <div>
        {uniqBy(displayErrors, ({ code }) => code).map((fieldError) => {
          const descriptionKey = `${fieldError.code}-description`;
          return (
            <ErrorComponent key={fieldError.code}>
              {fieldError?.isTranslated ? (
                <>{fieldError?.message}</>
              ) : (
                <>{t(`shared:errors.${fieldError.code}`, fieldError?.message || '', fieldError.tokens || {})}</>
              )}
              {showDescription && (
                <>
                  {i18n.exists(descriptionKey) && <div>{t(descriptionKey, fieldError.message || '')}</div>}
                  {fieldError.description && (
                    <div>
                      {fieldError.isTranslated ? fieldError.description : t(descriptionKey, fieldError.description)}
                    </div>
                  )}
                </>
              )}
            </ErrorComponent>
          );
        })}
      </div>
    </StyledErrorsList>
  );
}

export default FieldErrors;
